import { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import { Download, Filter, ArrowDownRight, TrendingDown, Users, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FunnelStage } from '@/hooks/useAnalytics';

interface ConversionFunnelProps {
  data: FunnelStage[];
  onExport?: () => void;
  isLoading?: boolean;
}

export function ConversionFunnel({
  data,
  onExport,
  isLoading = false,
}: ConversionFunnelProps) {
  const [hoveredStage, setHoveredStage] = useState<number | null>(null);

  const chartData = data.map((stage, index) => ({
    ...stage,
    index,
    fill: stage.color,
  }));

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: FunnelStage & { index: number } }> }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      const prevValue = item.index > 0 ? data[item.index - 1].value : item.value;
      const conversionRate = item.index > 0 && prevValue > 0 ? ((item.value / prevValue) * 100).toFixed(1) : '0';
      
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-card border border-border rounded-xl p-4 shadow-xl"
        >
          <p className="font-semibold text-foreground mb-2">{item.name}</p>
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center justify-between gap-8">
              <span className="text-muted-foreground">Count:</span>
              <span className="font-medium text-foreground">{item.count.toLocaleString()}</span>
            </div>
            {item.index > 0 && (
              <div className="flex items-center justify-between gap-8">
                <span className="text-muted-foreground">Conversion:</span>
                <span className="font-medium text-green-600 dark:text-green-400">{conversionRate}%</span>
              </div>
            )}
            {item.dropOffRate > 0 && (
              <div className="flex items-center justify-between gap-8">
                <span className="text-muted-foreground">Drop-off:</span>
                <span className="font-medium text-red-600 dark:text-red-400">{item.dropOffRate.toFixed(1)}%</span>
              </div>
            )}
          </div>
        </motion.div>
      );
    }
    return null;
  };

  const overallConversion = data.length > 0 && data[0].value > 0
    ? ((data[data.length - 1].value / data[0].value) * 100).toFixed(1)
    : '0';

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-[#2B7FFF]" />
            Conversion Funnel
          </CardTitle>
          <CardDescription>Lead to customer conversion stages</CardDescription>
        </div>
        <Button variant="outline" size="icon" onClick={onExport} className="h-9 w-9">
          <Download className="h-4 w-4" />
        </Button>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-3">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="p-3 rounded-xl bg-gradient-to-br from-[#2B7FFF]/5 to-[#2B7FFF]/10 border border-[#2B7FFF]/20 text-center"
          >
            <Users className="h-4 w-4 text-[#2B7FFF] mx-auto mb-1" />
            <p className="text-xs text-muted-foreground">Total Leads</p>
            <p className="text-lg font-bold text-foreground">
              {isLoading ? '-' : data[0]?.count.toLocaleString()}
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="p-3 rounded-xl bg-gradient-to-br from-green-500/5 to-green-500/10 border border-green-500/20 text-center"
          >
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 mx-auto mb-1" />
            <p className="text-xs text-muted-foreground">Closed Won</p>
            <p className="text-lg font-bold text-foreground">
              {isLoading ? '-' : data[data.length - 1]?.count.toLocaleString()}
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-3 rounded-xl bg-gradient-to-br from-amber-500/5 to-amber-500/10 border border-amber-500/20 text-center"
          >
            <TrendingDown className="h-4 w-4 text-amber-600 dark:text-amber-400 mx-auto mb-1" />
            <p className="text-xs text-muted-foreground">Conversion</p>
            <p className="text-lg font-bold text-foreground">
              {isLoading ? '-' : `${overallConversion}%`}
            </p>
          </motion.div>
        </div>

        {/* Funnel Visualization */}
        <div className="space-y-3">
          {isLoading ? (
            <div className="h-[200px] flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2B7FFF]" />
            </div>
          ) : (
            data.map((stage, index) => {
              const maxValue = data[0].value;
              const widthPercent = (stage.value / maxValue) * 100;
              const prevValue = index > 0 ? data[index - 1].value : stage.value;
              const stageConversion = index > 0 && prevValue > 0 ? ((stage.value / prevValue) * 100).toFixed(0) : '0';
              
              return (
                <motion.div
                  key={stage.name}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + index * 0.1, duration: 0.4 }}
                  className={cn(
                    'relative group cursor-pointer',
                    hoveredStage !== null && hoveredStage !== index && 'opacity-40'
                  )}
                  onMouseEnter={() => setHoveredStage(index)}
                  onMouseLeave={() => setHoveredStage(null)}
                >
                  {/* Stage Bar */}
                  <div className="flex items-center gap-3">
                    {/* Stage Name */}
                    <div className="w-28 flex-shrink-0">
                      <p className="text-sm font-medium text-foreground">{stage.name}</p>
                      <p className="text-xs text-muted-foreground">{stage.count.toLocaleString()}</p>
                    </div>
                    
                    {/* Bar Container */}
                    <div className="flex-1 h-10 bg-muted rounded-lg overflow-hidden relative">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${widthPercent}%` }}
                        transition={{ delay: 0.3 + index * 0.1, duration: 0.8, ease: 'easeOut' }}
                        className="h-full rounded-lg relative"
                        style={{ backgroundColor: stage.color }}
                      >
                        {/* Shine effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:animate-shimmer" />
                      </motion.div>
                      
                      {/* Percentage Label */}
                      <div className="absolute inset-0 flex items-center px-3">
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.6 + index * 0.1 }}
                          className="text-xs font-medium text-foreground"
                          style={{ marginLeft: `${widthPercent}%`, paddingLeft: widthPercent > 80 ? '0' : '8px' }}
                        >
                          {widthPercent.toFixed(0)}%
                        </motion.span>
                      </div>
                    </div>
                    
                    {/* Conversion Badge */}
                    <div className="w-20 flex-shrink-0 flex justify-end">
                      {index > 0 ? (
                        <Badge 
                          variant="secondary" 
                          className={cn(
                            'text-xs',
                            Number(stageConversion) >= 50 
                              ? 'bg-green-500/10 text-green-600 dark:text-green-400' 
                              : Number(stageConversion) >= 30 
                              ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                              : 'bg-red-500/10 text-red-600 dark:text-red-400'
                          )}
                        >
                          {stageConversion}%
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">Start</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Drop-off indicator */}
                  {index < data.length - 1 && stage.dropOffRate > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ delay: 0.5 + index * 0.1 }}
                      className="flex items-center gap-2 ml-28 my-1"
                    >
                      <ArrowDownRight className="h-3 w-3 text-red-500" />
                      <span className="text-xs text-red-600 dark:text-red-400">
                        {stage.dropOffRate.toFixed(1)}% drop-off
                      </span>
                    </motion.div>
                  )}
                </motion.div>
              );
            })
          )}
        </div>

        {/* Chart View */}
        {!isLoading && (
          <div className="h-[180px] mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
              >
                <XAxis type="number" hide />
                <YAxis
                  dataKey="name"
                  type="category"
                  tick={{ fill: 'hsl(var(--foreground))', fontSize: 11 }}
                  width={75}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="value"
                  radius={[0, 4, 4, 0]}
                  animationDuration={1200}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.fill}
                      opacity={hoveredStage === null || hoveredStage === index ? 1 : 0.4}
                      onMouseEnter={() => setHoveredStage(index)}
                      onMouseLeave={() => setHoveredStage(null)}
                    />
                  ))}
                  <LabelList
                    dataKey="count"
                    position="right"
                    fill="hsl(var(--muted-foreground))"
                    fontSize={11}
                    formatter={(value: number) => value.toLocaleString()}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ConversionFunnel;
